import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";

const CACHE = new Map();

function buildPath(points, width, height) {
  if (!Array.isArray(points) || points.length < 2) return "";
  const closes = points.map((p) => Number(p.c)).filter(Number.isFinite);
  if (closes.length < 2) return "";
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const span = Math.max(0.0001, max - min);
  const stepX = width / (closes.length - 1);

  return closes
    .map((close, index) => {
      const x = index * stepX;
      const y = height - ((close - min) / span) * height;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

function fmt(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "--";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export default function SymbolHoverChart({ symbol }) {
  const rootRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [points, setPoints] = useState([]);

  const ticker = String(symbol || "").trim().toUpperCase();
  const isOpen = hovered || pinnedOpen;

  useEffect(() => {
    if (!pinnedOpen) return undefined;
    function onPointerDown(event) {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setPinnedOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === "Escape") {
        setPinnedOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pinnedOpen]);

  async function loadOnce() {
    if (!ticker || loading || loaded) return;
    if (CACHE.has(ticker)) {
      setPoints(CACHE.get(ticker));
      setLoaded(true);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ period: "6mo", interval: "1d" });
      const response = await fetch(`/signals/indicators/${encodeURIComponent(ticker)}?${params.toString()}`);
      const payload = await response.json().catch(() => ({}));
      const raw = Array.isArray(payload?.history) ? payload.history : [];
      const mapped = raw
        .map((row) => ({
          t: row?.date,
          c: Number(row?.close),
        }))
        .filter((row) => Number.isFinite(row.c));
      CACHE.set(ticker, mapped);
      setPoints(mapped);
    } catch {
      CACHE.set(ticker, []);
      setPoints([]);
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  }

  const chart = useMemo(() => {
    if (!Array.isArray(points) || points.length < 2) return null;
    const viewW = 220;
    const viewH = 72;
    const d = buildPath(points, viewW, viewH);
    if (!d) return null;
    const first = Number(points[0]?.c);
    const last = Number(points[points.length - 1]?.c);
    if (!Number.isFinite(first) || !Number.isFinite(last) || first <= 0) return null;
    const deltaPct = ((last - first) / first) * 100;
    const up = deltaPct >= 0;
    return {
      d,
      first,
      last,
      deltaPct,
      up,
      stroke: up ? "#2e7d32" : "#ed6c02",
      viewW,
      viewH,
    };
  }, [points]);

  if (!ticker) {
    return <Typography fontWeight={600}>--</Typography>;
  }

  return (
    <Box
      ref={rootRef}
      sx={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => {
        setHovered(true);
        loadOnce();
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        component="button"
        type="button"
        onClick={() => {
          loadOnce();
          setPinnedOpen((v) => !v);
        }}
        aria-expanded={isOpen}
        aria-label={`${ticker} recent price chart`}
        sx={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          p: 0,
          m: 0,
          font: "inherit",
          color: "inherit",
          textDecoration: "underline dotted",
          textUnderlineOffset: "3px",
          fontWeight: 600,
        }}
      >
        {ticker}
      </Box>

      <Paper
        variant="outlined"
        sx={{
          display: isOpen ? "block" : "none",
          position: "absolute",
          left: 0,
          top: "100%",
          mt: 1,
          p: 1.25,
          width: 260,
          zIndex: 1200,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: 6,
        }}
      >
        <Typography variant="caption" fontWeight={700} color="text.primary">
          {ticker} - 6M Trend
        </Typography>

        {loading && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Loading chart...
          </Typography>
        )}

        {!loading && chart && (
          <>
            <svg viewBox={`0 0 ${chart.viewW} ${chart.viewH}`} style={{ width: "100%", height: 84, marginTop: 8 }}>
              <rect x="0" y="0" width={chart.viewW} height={chart.viewH} fill="#0f172a" rx="6" />
              <path d={chart.d} fill="none" stroke={chart.stroke} strokeWidth="2.4" strokeLinecap="round" />
            </svg>
            <Box sx={{ mt: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">${fmt(chart.first)}</Typography>
              <Typography variant="caption" sx={{ color: chart.up ? "success.main" : "warning.main", fontWeight: 700 }}>
                {chart.up ? "+" : ""}
                {fmt(chart.deltaPct, 1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">${fmt(chart.last)}</Typography>
            </Box>
          </>
        )}

        {!loading && !chart && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Price history is unavailable for this symbol right now.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
